import { gql } from './client';
import { emit, withExpected } from './events';
import type { PlanningAssessment } from './planningAssessments';
import type { PlanningDocument } from './planningDocuments';

/** Application: assessed from its documents. PreApplication: someone thinking of applying, assessed from their own description. */
export type CaseType = 'Application' | 'PreApplication';
/** A pre-application check needs at least this much description (the services enforce the same). */
export const MIN_PROPOSAL_LENGTH = 30;
/** A refinement only says what to change, so it can be much shorter (mirrors planning-case-service). */
export const MIN_REFINEMENT_LENGTH = 3;

export interface PlanningCase {
  id: string;
  name: string;
  tenantId: string;
  reference?: string;
  jurisdiction?: string;
  description?: string;
  /** Absent means Application. */
  caseType?: CaseType | null;
  ownerUserId?: string | null;
  nameProvisional?: boolean;
  assessmentStatus?: string;
  assessmentError?: string;
  latestScore?: number;
  latestRiskBand?: string;
  lastAssessedAt?: string;
  siteLocation?: string | null;
  deletedAt?: string | null;
  /** What the case has cost: metered model calls (JSON array, see api/caseCosts.ts) and premium OS map tiles. */
  usageLines?: string | null;
  osMapTiles?: number | null;
  osMapCostGbp?: number | null;
  osMapCachedTiles?: number | null;
  osMapCachedCostGbp?: number | null;
  tokensSpent?: number | null;
  tokensRefunded?: number | null;
  createdAt: string;
  updatedAt: string;
  version?: string;
}

export interface PlanningCaseDetail extends PlanningCase {
  policyInputs: { id: string; name: string; jurisdiction?: string; scope?: string; outputStatus?: string }[];
  documents: PlanningDocument[];
  assessments: Pick<PlanningAssessment, 'id' | 'ownerUserId' | 'status' | 'score' | 'riskBand' | 'proposalDescription' | 'proposalRefines' | 'assessedAt' | 'createdAt' | 'inputTokens' | 'outputTokens' | 'estimatedCostUsd'>[];
}

const FIELDS = `id name tenantId reference jurisdiction description caseType ownerUserId nameProvisional assessmentStatus assessmentError
  latestScore latestRiskBand lastAssessedAt deletedAt usageLines osMapTiles osMapCostGbp osMapCachedTiles osMapCachedCostGbp tokensSpent tokensRefunded createdAt updatedAt version`;

export interface PlanningCasePage {
  items: PlanningCase[];
  nextToken?: string;
}

export const CASE_PAGE_SIZE = 25;

// Newest first, one server page at a time; pass the previous page's nextToken for the next one.
// Every list is the caller's own cases unless an administrator's admin screen asks for scope "all".
export async function listPlanningCases(opts: { limit?: number; nextToken?: string; scope?: 'mine' | 'all' } = {}): Promise<PlanningCasePage> {
  const data = await gql<{ elevationsPlanningCases: { items: PlanningCase[]; nextToken: string | null } | null }>(
    `query ListPlanningCases($limit: Int, $nextToken: String, $scope: String) {
      elevationsPlanningCases(limit: $limit, nextToken: $nextToken, scope: $scope) { items { ${FIELDS} } nextToken }
    }`,
    { limit: opts.limit ?? CASE_PAGE_SIZE, nextToken: opts.nextToken ?? null, scope: opts.scope ?? 'mine' },
  );
  const page = data.elevationsPlanningCases;
  return { items: page?.items ?? [], nextToken: page?.nextToken ?? undefined };
}

export async function getPlanningCase(id: string): Promise<PlanningCaseDetail | undefined> {
  const data = await gql<{ elevationsPlanningCase: PlanningCaseDetail | null }>(
    `query GetPlanningCase($id: ID!) {
      elevationsPlanningCase(id: $id) {
        ${FIELDS} siteLocation
        policyInputs { id name jurisdiction scope outputStatus }
        documents { id name category kind filename contentType size s3Key scanStatus scanDetail createdAt updatedAt version }
        assessments { id ownerUserId status score riskBand proposalDescription proposalRefines assessedAt createdAt inputTokens outputTokens estimatedCostUsd }
      }
    }`,
    { id },
  );
  const c = data.elevationsPlanningCase;
  if (!c) return undefined;
  return { ...c, policyInputs: c.policyInputs ?? [], documents: c.documents ?? [], assessments: c.assessments ?? [] };
}

export function emitCreatePlanningCase(
  payload: { name: string; nameProvisional?: boolean; tenantId: string; reference?: string; jurisdiction?: string; description?: string; caseType?: CaseType; performedBy?: string },
  connectionId?: string,
): Promise<string> {
  return emit('CreatePlanningCase', crypto.randomUUID(), payload, connectionId);
}

export function emitUpdatePlanningCase(
  id: string,
  patch: { name?: string; reference?: string; jurisdiction?: string; description?: string; descriptionRefines?: boolean; performedBy?: string },
  connectionId?: string,
  expectedValues?: Record<string, unknown>,
): Promise<string> {
  return emit('UpdatePlanningCase', id, withExpected(patch, expectedValues), connectionId);
}

export function emitDeletePlanningCase(id: string, performedBy?: string, connectionId?: string): Promise<string> {
  return emit('DeletePlanningCase', id, { performedBy }, connectionId);
}

// Hides the case from the user's lists; the case and its data are kept. Admins remove cases for good with emitDeletePlanningCase.
export function emitSoftDeletePlanningCase(id: string, performedBy?: string, connectionId?: string): Promise<string> {
  return emit('SoftDeletePlanningCase', id, { performedBy }, connectionId);
}

export function emitCancelPlanningCase(id: string, performedBy?: string, connectionId?: string): Promise<string> {
  return emit('CancelPlanningCase', id, { performedBy }, connectionId);
}

export function emitLinkPolicyInput(caseId: string, policyInputId: string, linked: boolean, connectionId?: string): Promise<string> {
  return emit(linked ? 'PlanningCasePolicyInputLinked' : 'PlanningCasePolicyInputUnlinked', caseId,
    { planningCaseId: caseId, policyInputId }, connectionId);
}

export function emitLinkDocument(caseId: string, documentId: string, linked: boolean, connectionId?: string): Promise<string> {
  return emit(linked ? 'PlanningCaseDocumentLinked' : 'PlanningCaseDocumentUnlinked', caseId,
    { planningCaseId: caseId, documentId }, connectionId);
}
